/**
 * Mana Locals Admin — M83 agreements, PLATFORM-operator wire contracts.
 *
 * CONTRACT SOURCE: `contracts/agreements.register.md` (register v1.6) and the API's
 * `src/domains/m83-agreements/contracts/agreements.schemas.ts`. The API schema is the source
 * of truth; this file mirrors it.
 *
 * These routes are the platform-operator surface: authoring documents, publishing versions,
 * configuring requirements, and reading the deployment-wide execution ledger and the RESTRICTED
 * evidence package. They require a live platform grant and must NEVER be called from a
 * vendor/customer surface.
 *
 * Nothing in this file is an authorization statement. Zod validates SHAPE only.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';
import {
  AgreementAudienceSchema,
  AgreementDocumentKeySchema,
  AgreementDocumentStatusSchema,
  AgreementExecutionEventResponseSchema,
  AgreementExecutionResponseSchema,
  AgreementLocaleSchema,
  AgreementRequiredActionSchema,
  AgreementSha256Schema,
  AgreementSigningMethodSchema,
  AgreementSubjectKindSchema,
  AgreementExecutionStatusSchema,
  AgreementLocationStateSchema,
  AgreementVersionResponseSchema,
  SigningVerificationPolicySchema,
} from './agreements';

/* --------------------------------------------------------------------------------------------
 * Request bodies
 * ------------------------------------------------------------------------------------------ */

/** `POST /platform/agreement-documents` */
export const CreateAgreementDocumentRequestSchema = z.object({
  documentKey: AgreementDocumentKeySchema,
  title: z.string().min(1).max(300),
  audience: AgreementAudienceSchema,
  requiredAction: AgreementRequiredActionSchema,
});

/** `POST /platform/agreement-documents/{documentId}/status` */
export const SetAgreementDocumentStatusRequestSchema = z.object({
  status: AgreementDocumentStatusSchema,
});

/**
 * One attachment on a version.
 *
 * The API requires `ordinal` and `objectKey`; the handoff document's §1.2 shows only
 * `label`/`mimeType`/`byteSize`/`contentHash`. The API schema wins: an attachment is a
 * reference to an already-uploaded object, not an inline blob.
 */
export const AgreementAttachmentInputSchema = z.object({
  ordinal: z.number().int().min(0).max(19),
  label: z.string().min(1).max(200),
  objectKey: z.string().min(1).max(1024),
  mimeType: z.string().min(1).max(200),
  byteSize: z.number().int().positive(),
  contentHash: AgreementSha256Schema,
});

/**
 * `POST /platform/agreement-documents/{documentId}/versions`
 *
 * The API does NOT accept a `version` number in the body — the server assigns the next version.
 * The handoff document's §1.2 shows `version`; the API schema wins.
 */
export const PublishAgreementVersionRequestSchema = z.object({
  locale: AgreementLocaleSchema,
  isDefaultLocale: z.boolean(),
  title: z.string().min(1).max(300),
  /**
   * HTML string. The console authors it with the shared rich text editor and the
   * API stores it verbatim under its content hash; the display path re-parses
   * stored HTML through the editor's own schema, which is where sanitisation
   * happens. Still non-empty: a version with no content is not a version.
   */
  content: z.string().min(1),
  effectiveAt: IsoDateTimeSchema,
  attachments: z.array(AgreementAttachmentInputSchema).max(20).default([]),
});

/** `POST /platform/agreement-versions/{versionId}/withdrawal` */
export const WithdrawAgreementVersionRequestSchema = z.object({
  reason: z.string().min(1).max(1000),
});

/**
 * `POST /platform/agreement-requirements`
 *
 * `verificationPolicy` is OPTIONAL on the wire, but MANDATORY for a VENDOR SIGNATURE agreement
 * (audience `tenant`, requiredAction `signature`): the operator must state the policy on purpose.
 * Omitting it for that shape is refused with `INVALID_AGREEMENT_REQUIREMENT` at
 * `/verificationPolicy` rather than silently defaulting to `session_reattested`. For every other
 * shape an absent value defaults to `session_reattested`.
 *
 * The UI must therefore require the operator to choose `fresh_strong` explicitly for a vendor
 * signature agreement, and must never send an absent policy for that shape.
 */
export const CreateAgreementRequirementRequestSchema = z.object({
  documentId: UuidSchema,
  versionId: UuidSchema,
  audience: z.enum(['tenant', 'customer']),
  requiredAction: AgreementRequiredActionSchema,
  applicability: z.record(z.string(), z.unknown()).default({}),
  reacceptanceRequired: z.boolean().default(true),
  deadlineAt: IsoDateTimeSchema.nullable().default(null),
  gracePeriodSeconds: z.number().int().min(0).nullable().default(null),
  verificationPolicy: SigningVerificationPolicySchema.optional(),
});

/* --------------------------------------------------------------------------------------------
 * Response bodies
 * ------------------------------------------------------------------------------------------ */

export const AgreementDocumentResponseSchema = z.object({
  documentId: UuidSchema,
  documentKey: AgreementDocumentKeySchema,
  title: z.string(),
  audience: AgreementAudienceSchema,
  requiredAction: AgreementRequiredActionSchema,
  status: AgreementDocumentStatusSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const AgreementRequirementResponseSchema = z.object({
  requirementId: UuidSchema,
  documentId: UuidSchema,
  versionId: UuidSchema,
  audience: z.enum(['tenant', 'customer']),
  requiredAction: AgreementRequiredActionSchema,
  applicability: z.record(z.string(), z.unknown()),
  reacceptanceRequired: z.boolean(),
  deadlineAt: IsoDateTimeSchema.nullable(),
  gracePeriodSeconds: z.number().int().nullable(),
  verificationPolicy: SigningVerificationPolicySchema,
  active: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});

export const AgreementDocumentListResponseSchema = z.object({
  documents: z.array(AgreementDocumentResponseSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

/**
 * `GET /platform/agreement-documents/{documentId}/versions`
 *
 * CONTRACT SOURCE: register §2.16 (`EP-listAgreementVersions`, added in v1.6) and
 * `AgreementVersionListResponse` (§3.2.15). The collection is under a NAMED key (`versions`),
 * with pagination INSIDE `data` — not a bare array, and not `items`.
 *
 * Rows are the version response the publish/withdraw commands already return
 * (`AgreementVersionResponseSchema`, `./agreements` §3.2.3), reused rather than re-declared so
 * the three routes cannot drift from one another.
 *
 * The server returns every version of the document, so `nextCursor` is `null` and `hasMore` is
 * `false` because it emitted all of them — a truthful statement, not a defaulted page. The order
 * is the server's (`version DESC, locale ASC`); the client never re-sorts.
 */
export const AgreementVersionListResponseSchema = z.object({
  versions: z.array(AgreementVersionResponseSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export const AgreementExecutionListResponseSchema = z.object({
  executions: z.array(AgreementExecutionResponseSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

/**
 * The RESTRICTED evidence package for one execution.
 *
 * This is a SEPARATE representation from the subject-facing execution detail: it carries the
 * contextual facts (IP, user agent, coarse device category, client timezone, location) that a
 * subject is never shown about itself and that a platform operator may only read under the
 * narrower `platform.agreement.evidence.read` grant. Every field is nullable because a fact that
 * was not collected is recorded as absent, never fabricated.
 */
export const AgreementEvidencePackageSchema = z.object({
  executionId: UuidSchema,
  executionRef: z.string(),
  subjectKind: AgreementSubjectKindSchema,
  subjectId: UuidSchema,
  status: AgreementExecutionStatusSchema,
  signingMethod: AgreementSigningMethodSchema.nullable(),
  contentHash: AgreementSha256Schema,
  signatureHash: AgreementSha256Schema.nullable(),
  pdfHash: AgreementSha256Schema.nullable(),
  completedAt: IsoDateTimeSchema.nullable(),
  evidence: z
    .object({
      observedIp: z.string().nullable(),
      userAgent: z.string().nullable(),
      deviceCategory: z.string().nullable(),
      clientTimezone: z.string().nullable(),
      locationState: AgreementLocationStateSchema,
      locationLatitude: z.number().nullable(),
      locationLongitude: z.number().nullable(),
      locationAccuracyMeters: z.number().nullable(),
      locationSource: z.string().nullable(),
      locationCapturedAt: IsoDateTimeSchema.nullable(),
      verificationEventRef: z.string().nullable(),
      recordedAt: IsoDateTimeSchema,
    })
    .nullable(),
  signatureAsset: z
    .object({
      assetId: UuidSchema,
      mimeType: z.string(),
      byteSize: z.number().int(),
      contentHash: AgreementSha256Schema,
      captureMethod: AgreementSigningMethodSchema,
      createdAt: IsoDateTimeSchema,
    })
    .nullable(),
  pdfDownloadUrl: z.string().nullable(),
  signatureDownloadUrl: z.string().nullable(),
  events: z.array(AgreementExecutionEventResponseSchema),
});

export const AgreementEvidencePackageResponseSchema = z.object({
  evidencePackage: AgreementEvidencePackageSchema,
});

/* --------------------------------------------------------------------------------------------
 * Inferred types
 * ------------------------------------------------------------------------------------------ */

export type CreateAgreementDocumentRequest = z.infer<
  typeof CreateAgreementDocumentRequestSchema
>;
export type SetAgreementDocumentStatusRequest = z.infer<
  typeof SetAgreementDocumentStatusRequestSchema
>;
export type AgreementAttachmentInput = z.infer<typeof AgreementAttachmentInputSchema>;
export type PublishAgreementVersionRequest = z.infer<
  typeof PublishAgreementVersionRequestSchema
>;
export type WithdrawAgreementVersionRequest = z.infer<
  typeof WithdrawAgreementVersionRequestSchema
>;
export type CreateAgreementRequirementRequest = z.infer<
  typeof CreateAgreementRequirementRequestSchema
>;

export type AgreementDocumentResponse = z.infer<typeof AgreementDocumentResponseSchema>;
export type AgreementRequirementResponse = z.infer<typeof AgreementRequirementResponseSchema>;
export type AgreementDocumentListResponse = z.infer<
  typeof AgreementDocumentListResponseSchema
>;
export type AgreementVersionListResponse = z.infer<
  typeof AgreementVersionListResponseSchema
>;
export type AgreementExecutionListResponse = z.infer<
  typeof AgreementExecutionListResponseSchema
>;
export type AgreementEvidencePackage = z.infer<typeof AgreementEvidencePackageSchema>;
export type AgreementEvidencePackageResponse = z.infer<
  typeof AgreementEvidencePackageResponseSchema
>;
